import { faDatabase } from '@fortawesome/free-solid-svg-icons';
import useModalManager from '@hooks/useModalManager';
import { Badge } from '@ui';
import { Booking } from 'components/EventCalendar/types';
import { PaymentStatusBadge } from 'components/MollieConnect/PaymentStatusBadge';
import moment from 'moment';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Column, DataTable, DataTableContainer, TableActionButton } from '../../../DataTable';
import { DatesComponent } from '../../../EventCalendar/Booking/BookingList/DatesComponent';
import { ClubMember } from '../../types';
import BookingFormDataModal from './BookingFormDataModal';

// The associated-data endpoint adds this flag so the list can tell booker from participant.
type MemberBooking = Booking & { is_booker?: boolean };

interface Props {
    member: ClubMember;
}

// Lists a member's EventCalendar bookings (calendar_event_bookings) — the "Buchungen" tab of the member preview.
const ClubMemberBookingsList = ({ member }: Props) => {
    const { t } = useTranslation('bookings');
    const { addModal } = useModalManager();
    const asyncListRef = useRef<any>(undefined);

    // Opens the booking's form data in a modal, from where a member can be created or updated.
    const handleShowFormData = (booking: MemberBooking) =>
        addModal({
            title: t('booking.list.modals.formData.title'),
            content: (
                <BookingFormDataModal
                    bookingId={booking.id}
                    eventId={booking.event.id}
                    formDataId={booking.form_data_id}
                    formId={booking.event.form_id}
                    onMemberChanged={() => asyncListRef.current?.reload()}
                />
            ),
            size: 50,
            show: true
        });

    const columns: Column<MemberBooking>[] = [
        {
            name: 'Veranstaltung',
            uid: 'event',
            width: '32%',
            renderer: (event) => (
                <span className="tw-block tw-whitespace-normal tw-break-words tw-leading-tight">
                    {event?.title ?? '-'}
                </span>
            )
        },
        {
            name: 'Termin',
            uid: 'start',
            width: '22%',
            renderer: (_, item) => (item?.event ? <DatesComponent booking={item} /> : '-')
        },
        {
            // Role and status share a cell: both are short badges that wasted a full column each.
            name: 'Status',
            uid: 'cancelled_at',
            width: '15%',
            renderer: (cancelledAt, item) => (
                <div className="tw-flex tw-flex-col tw-items-start tw-gap-1">
                    {cancelledAt ? (
                        <Badge color="error" flat>
                            Storniert
                        </Badge>
                    ) : item?.is_waiting ? (
                        <Badge color="warning" flat>
                            Warteliste
                        </Badge>
                    ) : (
                        <Badge color="success" flat>
                            Gebucht
                        </Badge>
                    )}
                    {item?.is_booker ? (
                        <Badge color="secondary" flat>
                            Bucher
                        </Badge>
                    ) : (
                        <Badge color="light" flat>
                            Teilnehmer
                        </Badge>
                    )}
                </div>
            )
        },
        {
            name: 'Zahlung',
            uid: 'payment_status',
            width: '13%',
            renderer: (status, item) =>
                status ? <PaymentStatusBadge status={status} paymentId={item?.payment_id} /> : '-'
        },
        {
            name: 'Gebucht am',
            uid: 'created_at',
            width: '12%',
            renderer: (value) =>
                value ? (
                    <span className="tw-block tw-whitespace-normal tw-leading-tight">
                        {moment(value).format('DD.MM.YYYY')}
                        <span className="tw-block tw-text-xs tw-text-accents-6">{moment(value).format('HH:mm')}</span>
                    </span>
                ) : (
                    '-'
                )
        },
        { name: 'Optionen', uid: 'actions', align: 'end', width: '6%' }
    ];

    return (
        <DataTableContainer>
            <DataTable
                hideSearchBar
                className="tw-h-full"
                fetchUrl={`club-member/${member.id}/associated-data/bookings`}
                columns={columns}
                asyncListRef={asyncListRef}
                tableProps={{
                    showSelectionCheckboxes: false,
                    sticked: true,
                    compact: false
                }}
                rowActions={(item: MemberBooking) => (
                    <TableActionButton.Container justify="flex-end">
                        {item.form_data_id && item.event && (
                            <TableActionButton
                                icon={faDatabase}
                                tooltip={t('booking.list.rowActions.formData')}
                                onPress={() => handleShowFormData(item)}
                            />
                        )}
                    </TableActionButton.Container>
                )}
            />
        </DataTableContainer>
    );
};

export default ClubMemberBookingsList;
